import { getNfcCardNoSchema } from "Components/Paypoints/schema";
import type { PayPointBaseDto } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { toast } from "sonner";
import { useIsCurrentUserMerchant } from "Hooks/useIsCurrentUserMerchant";
import { useAssignNFCToPayPoint } from "Api/Mutations/PayPoints/useAssignNFCToPayPoint";
import { PaypointsGroup } from "Components/Shared/PaypointsGroup";

type Props = {
  title: string;
  limit: number;
  paypoints: Array<PayPointBaseDto>;
  productID: number | null;
  nfcCardNo: string;
  merchantPublicID?: string;
};

export const PaypointsNfcAssignList = ({
  title,
  paypoints,
  limit,
  productID,
  nfcCardNo,
  merchantPublicID,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useResource();
  const isMerchant = useIsCurrentUserMerchant();
  const { mutate: updatePayPoint } = useAssignNFCToPayPoint({
    onSuccess: () => {
      toast.success(t(Resources.Nfc.CardAssignedSuccessfully));
      const url = isMerchant
        ? appUrl("paypoints")
        : `${appUrl("merchant-detail")}/${merchantPublicID}?activeTab=paypoints`;
      navigate(url);
    },
    onError: () => {
      toast.error(t(Resources.Nfc.CardAssignmentFailed));
    },
  });

  const isAddButtonVisible = paypoints.length < limit;

  const assignNfcCard = (paypoint: PayPointBaseDto) => {
    const isNfcCardNoValid = getNfcCardNoSchema(t).safeParse(nfcCardNo).success;

    if (!isNfcCardNoValid) {
      toast.error(t(Resources.Nfc.CardAssignmentFailedFormatError));
      return;
    }

    const canUpdate = paypoint.nfcCardNo
      ? window.confirm(t(Resources.Nfc.CardOverwriteConfirmation, { name }))
      : true;

    if (canUpdate) {
      updatePayPoint({
        publicID: paypoint.publicID,
        data: { nfcCardNo },
      });
    }
  };

  const handleCreatePaypoint = () => {
    const params = new URLSearchParams();

    if (productID) {
      params.set("productID", productID.toString());
    }
    if (merchantPublicID) {
      params.set("merchantPublicID", merchantPublicID);
    }
    if (nfcCardNo) {
      params.set("nfcCardNo", nfcCardNo);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${appUrl("new-paypoint")}?${queryString}`
      : appUrl("new-paypoint");

    navigate(url);
  };

  return (
    <PaypointsGroup
      title={title}
      paypoints={paypoints}
      limit={limit}
      onPaypointClick={assignNfcCard}
      onAddPaypoint={isAddButtonVisible ? handleCreatePaypoint : undefined}
    />
  );
};
